import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping } from '@repo/toolkit/colors'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column, FilterOptions } from '@repo/ui/data-table'
import { BagIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import React from 'react'
import { Link } from 'react-router-dom'

import { getAffectedStaffReminders } from '../../../api/affected-staffs/get-affected-staff-reminders'
import { API } from '../../../api/apis.config'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { reportStatusEnumMapping } from '../../../lib/types/enums/affected-staff/report-status.enum'
import {
  AllStaffDocumentTypeEnum,
  getAllStaffDocumentMapping
} from '../../../lib/types/enums/common/document-status.enum'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../elements/country-flag'
import DynamicDataTable, { DynamicTableRef, TableAction } from '../../elements/dynamic-table'
import { countFilters } from '../../elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../elements/information-tooltip'
import PeriodBadge from '../../elements/period-badge'
import { QualificationBadge } from '../../elements/qualification-badge'
import StaffLatestReminderCell from '../../elements/staff-latest-reminder-cell'

type Props = {
  actions?: (tableRef: React.RefObject<DynamicTableRef>) => TableAction<AffectedStaff, undefined>[]
  filterOptions: FilterOptions<AffectedStaffFilters>
  allowSearchParam?: boolean
  openInNewTab?: boolean
}

// Lets a badge break on several lines instead of widening its column
const WRAPPED_BADGE_CLASS = 'h-auto min-h-7 py-1 whitespace-normal'

const getAffectedStaffViewPath = (id: number) =>
  Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', id.toString())

/**
 * Renders the cell content as a real anchor so the browser context menu
 * ("Open link in new tab") and cmd/ctrl+click work natively.
 */
const AffectedStaffLink = ({
  id,
  openInNewTab,
  children
}: {
  id: number
  openInNewTab?: boolean
  children: React.ReactNode
}) => (
  <Link
    to={getAffectedStaffViewPath(id)}
    target={openInNewTab ? '_blank' : undefined}
    onClick={(event) => event.stopPropagation()}
  >
    {children}
  </Link>
)

const AffectedStaffTable = ({
  actions,
  filterOptions,
  allowSearchParam = true,
  openInNewTab = false,
  ...props
}: Props) => {
  const tableRef = React.useRef<DynamicTableRef<AffectedStaff>>(null)
  const { navigateWithEvent } = useNavigator()
  const { t } = useTranslate()

  useObserver({
    key: 'affected-staffs',
    update: () => tableRef.current?.refetch()
  })

  const columns: Column<AffectedStaff>[] = [
    {
      enableSorting: true,
      accessorKey: 'staff.lastName',
      header: 'NOM',
      cell: ({ row }) => (
        <AffectedStaffLink id={row.original.id} openInNewTab={openInNewTab}>
          {row.original.status === AffectedStaffStatusEnum.PENDING ? (
            <InformationToolTip
              icon={<Badge text={row.original.staff.lastName.toUpperCase()} variant={'warning'} />}
              size="small"
            >
              <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
                Non confirmé
              </Typography>
            </InformationToolTip>
          ) : (
            row.original.staff.lastName.toUpperCase()
          )}
        </AffectedStaffLink>
      )
    },
    {
      enableSorting: true,
      accessorKey: 'staff.firstName',
      header: 'Prénom',
      cell: ({ row }) => (
        <AffectedStaffLink id={row.original.id} openInNewTab={openInNewTab}>
          {row.original.status === AffectedStaffStatusEnum.PENDING ? (
            <InformationToolTip
              icon={
                <Badge
                  text={StringFormatter.capitalize(row.original.staff.firstName)}
                  variant={'warning'}
                />
              }
              size="small"
            >
              <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
                Non confirmé
              </Typography>
            </InformationToolTip>
          ) : (
            row.original.staff.firstName
          )}
        </AffectedStaffLink>
      )
    },
    {
      enableSorting: true,
      accessorKey: 'staff.position',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.staff.position])
    },
    {
      accessorKey: 'staffRequest.qualification',
      header: 'Qualifications',
      cell: ({ row }) => (
        <QualificationBadge
          qualification={row.original.staffRequest.qualification}
          className={WRAPPED_BADGE_CLASS}
        />
      )
    },
    {
      accessorKey: 'staffRequest.country',
      header: 'Pays',
      cell: ({ row }) => <CountryFlag code={row.original.staffRequest.country?.code} />
    },
    {
      accessorKey: 'staffRequest.centers',
      header: 'Centre',
      cell: ({ row }) => {
        const centers = row.original.staffRequest.centers
        const oldCenter = row.original.staffRequest.oldCenter
        if (centers.length === 0)
          return oldCenter ? (
            <Badge
              text={oldCenter}
              variant={'white'}
              className={cn(WRAPPED_BADGE_CLASS, 'max-w-36')}
            />
          ) : (
            '-'
          )
        return centers.map((center, index) => (
          <Badge
            key={index}
            text={center.name}
            variant={'white'}
            className={cn(WRAPPED_BADGE_CLASS, 'max-w-36')}
          />
        ))
      }
    },
    {
      enableSorting: true,
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ row }) => {
        const centers = row.original.staffRequest.centers
        const dates = centers
          .flatMap((center) => center.centerToJourneys.map((ctj) => ctj.journey.date))
          .filter((date, index, self) => index === self.findIndex((d) => d.id === date.id))

        return (
          <div className="flex flex-col gap-1">
            {dates.map((date) => {
              const period = date.period
              // Non-breaking space: the badge only breaks after the dash, on 2 lines
              const dateString = `${DateFormatter.DDMMYYYY(date.departureDate)}\u00A0- ${DateFormatter.DDMMYYYY(date.returnDate)}`
              if (!period)
                return <Badge key={date.id} text={dateString} className={WRAPPED_BADGE_CLASS} />
              return (
                <PeriodBadge
                  key={date.id}
                  period={period}
                  text={dateString}
                  className={WRAPPED_BADGE_CLASS}
                />
              )
            })}
          </div>
        )
      }
    },
    {
      accessorKey: 'staffRequest.formulas',
      header: 'Formule',
      cell: ({ row }) => (
        <div className="text-wrap w-24">
          {row.original.staffRequest.formulas.length > 0
            ? row.original.staffRequest.formulas
                .map((formula) => getTranslatedName(formula))
                .join(', ')
            : '-'}
        </div>
      )
    },
    {
      enableSorting: true,
      accessorKey: 'viewData.travelLabel',
      header: 'Voyage',
      // Computed by the backend (trip label, or the travel choice of foreign staff); the
      // transport mode comes on its own line
      cell: ({ row }) => (
        <div className="whitespace-pre-line min-w-24">
          {valueRender(row.original.viewData?.travelLabel)}
        </div>
      )
    },
    {
      accessorKey: 'missingDocumentsReminders',
      header: 'Relance doc',
      cell: ({ row }) => (
        <StaffLatestReminderCell
          reminders={row.original.missingDocumentsReminders}
          type={'MISSING_DOCUMENTS'}
          historyQueryKey={['affected-staff-reminders', row.original.id]}
          getHistory={() => getAffectedStaffReminders(row.original.id)}
          wrap
        />
      )
    },
    {
      accessorKey: 'contractReminders',
      header: 'Relance contrat',
      cell: ({ row }) => (
        <StaffLatestReminderCell
          reminders={row.original.contractReminders}
          type={'CONTRACT'}
          historyQueryKey={['affected-staff-reminders', row.original.id]}
          getHistory={() => getAffectedStaffReminders(row.original.id)}
          wrap
        />
      )
    },
    {
      accessorKey: '',
      header: 'Documents',
      cell: ({ row }) => {
        const staff = row.original.staff
        const documents = getAllStaffDocumentMapping(
          staff,
          row.original.contract,
          row.original.staffRequest.country
        )

        return (
          // 2 rows of icons keep the column narrow
          <div className="grid grid-cols-4 gap-1 w-max">
            {(Object.keys(documents) as AllStaffDocumentTypeEnum[])
              // The signed contract icon is shown in the "Contrat signé" column
              .filter((key) => key !== AllStaffDocumentTypeEnum.SIGNED_CONTRACT)
              .map((key) => (
                <InformationToolTip
                  key={key}
                  icon={
                    <Badge
                      variant={documents[key].variant}
                      icon={documents[key].icon}
                      iconPosition="start"
                      className="px-1"
                    />
                  }
                  size="small"
                >
                  <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
                    {documents[key].label}
                  </Typography>
                </InformationToolTip>
              ))}
          </div>
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'contract.contractSentAt',
      header: 'Envoi contrat',
      cell: ({ row }) => {
        const contractSentAt = row.original.contract?.contractSentAt
        return contractSentAt ? (
          <Badge text={DateFormatter.DDMMYYYY(contractSentAt)} variant={'success'} />
        ) : (
          <Badge text={'Non'} variant={'danger'} />
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'contract.contractSignedAt',
      header: 'Contrat signé',
      cell: ({ row }) => {
        const contractSignedAt = row.original.contract?.contractSignedAt
        const signedContract = getAllStaffDocumentMapping(
          row.original.staff,
          row.original.contract,
          row.original.staffRequest.country
        )[AllStaffDocumentTypeEnum.SIGNED_CONTRACT]

        return (
          <div className="flex flex-col items-start gap-1 py-2">
            <InformationToolTip
              icon={
                <Badge
                  variant={signedContract.variant}
                  icon={signedContract.icon}
                  iconPosition="start"
                />
              }
              size="small"
            >
              <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
                {signedContract.label}
              </Typography>
            </InformationToolTip>
            {contractSignedAt ? (
              <Badge text={DateFormatter.DDMMYYYY(contractSignedAt)} variant={'success'} />
            ) : (
              <Badge text={'Non'} variant={'danger'} />
            )}
          </div>
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'dpaeDownloadAt',
      header: 'DPAE',
      cell: ({ row }) => {
        // Foreign (english) staff are not hired under french law, so they have no DPAE
        if (StaffRequestProperties.isForeignStaff(row.original.staffRequest))
          return <Badge text={'N/A'} variant={'grey'} />

        return row.original.dpaeDownloadAt ? (
          <Badge text={DateFormatter.DDMMYYYY(row.original.dpaeDownloadAt)} variant={'success'} />
        ) : (
          <Badge text={'Non'} variant={'danger'} />
        )
      }
    },
    {
      enableSorting: false,
      accessorKey: 'endOfStayReportStatus',
      // Zero-width space: lets the header break after the slash, on 2 lines
      header: 'Sacoche/\u200BRapport',
      cell: ({ row }) => {
        const status = row.original.endOfStayReportStatus
        const color = colorsMapping[reportStatusEnumMapping[status].variant || 'grey']
        return (
          <div
            className="flex justify-center items-center w-8 h-8 p-1 rounded-sm"
            style={{ background: color.background }}
          >
            <BagIcon color={color.text} />
          </div>
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'affectedAt',
      header: "Date d'affectation",
      cell: ({ row }) =>
        row.original.affectedAt ? (
          <Badge text={DateFormatter.DDMMYYYY(row.original.affectedAt)} />
        ) : (
          '-'
        )
    },
    {
      enableSorting: true,
      accessorKey: 'affectedBy.lastName',
      header: 'Login',
      cell: ({ row }) => (
        <div className="whitespace-normal">
          {toFullName(row.original.affectedBy?.firstName, row.original.affectedBy?.lastName, {
            options: { mode: 'lastname-firstname' }
          })}
        </div>
      )
    }
  ]

  return (
    <DynamicDataTable<AffectedStaff, AffectedStaffFilters>
      {...props}
      ref={tableRef}
      title={'Tous les encadrants'}
      onLoadingChange={filterOptions?.setIsTableLoading}
      columns={columns}
      displayOrder={'before-select'}
      // Tighter cells so every column fits on a 23-inch screen
      tableClassName="[&_td]:pl-1 [&_th]:pl-1"
      selectable
      allowSearchParam={allowSearchParam}
      onRowClick={(row, event) => {
        const path = getAffectedStaffViewPath(row.id)
        if (openInNewTab) window.open(path, '_blank')
        else navigateWithEvent(path, event)
      }}
      version={'FULL'}
      permissions={{ delete: false }}
      endpoint={API.affectedStaffs.getAll.url}
      filters={filterOptions?.selectedFilter}
      filtersLength={countFilters(filterOptions?.selectedFilter, filterOptions?.discardedFilters)}
      onFiltersClick={filterOptions?.onFiltersClick}
      actions={actions ? actions(tableRef) : undefined}
    />
  )
}

export default AffectedStaffTable
